import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Radio,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../../utils/api';

const { Option } = Select;

const HomeFunctionButtons = () => {
  const [loading, setLoading] = useState(false);
  const [buttons, setButtons] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [form] = Form.useForm();

  // 加载数据
  const fetchButtons = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/home/function-buttons');
      if (res.code === 200) {
        setButtons(res.data.buttons || []);
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchButtons();
  }, []);

  // 打开新增/编辑弹窗
  const handleEdit = (record = null) => {
    setEditingButton(record);
    if (record) {
      form.setFieldsValue({
        buttonName: record.button_name,
        iconUrl: record.icon_url,
        iconType: record.icon_type,
        linkType: record.link_type,
        linkPath: record.link_path,
        sortOrder: record.sort_order,
        status: record.status === 1
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        buttonName: values.buttonName,
        iconUrl: values.iconUrl,
        iconType: values.iconType,
        linkType: values.linkType,
        linkPath: values.linkPath,
        sortOrder: values.sortOrder || 0,
        status: values.status ? 1 : 0
      };

      if (editingButton) {
        // 编辑
        await api.put(`/admin/home/function-buttons/${editingButton.id}`, payload);
        message.success('更新成功');
      } else {
        // 新增
        await api.post('/admin/home/function-buttons', payload);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchButtons();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/home/function-buttons/${id}`);
      message.success('删除成功');
      fetchButtons();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 切换状态
  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.status === 1 ? 0 : 1;
      await api.patch(`/admin/home/function-buttons/${record.id}/status`, {
        status: newStatus
      });
      message.success('状态更新成功');
      fetchButtons();
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  // 上移/下移
  const handleMove = async (record, direction) => {
    const currentIndex = buttons.findIndex(b => b.id === record.id);
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === buttons.length - 1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newButtons = [...buttons];
    [newButtons[currentIndex], newButtons[targetIndex]] = [newButtons[targetIndex], newButtons[currentIndex]];
    
    setButtons(newButtons);

    // 更新后端排序
    const sortData = newButtons.map((item, index) => ({
      id: item.id,
      sortOrder: index + 1
    }));

    try {
      await api.post('/admin/home/function-buttons/sort', { sortData });
      message.success('排序更新成功');
    } catch (error) {
      message.error('排序更新失败');
      fetchButtons(); // 恢复原排序
    }
  };

  // 刷新缓存
  const handleRefreshCache = async () => {
    try {
      const res = await api.post('/admin/home/function-buttons/refresh-cache');
      if (res.code === 200) {
        message.success(res.data?.message || '缓存刷新成功');
      }
    } catch (error) {
      message.error('缓存刷新失败');
    }
  };

  const columns = [
    {
      title: '排序',
      dataIndex: 'sort_order',
      width: 120,
      render: (_, record, index) => (
        <Space>
          <Button
            size="small"
            icon={<UpOutlined />}
            disabled={index === 0}
            onClick={() => handleMove(record, 'up')}
          />
          <Button
            size="small"
            icon={<DownOutlined />}
            disabled={index === buttons.length - 1}
            onClick={() => handleMove(record, 'down')}
          />
        </Space>
      )
    },
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '按钮名称',
      dataIndex: 'button_name',
      width: 150
    },
    {
      title: '图标',
      dataIndex: 'icon_url',
      width: 100,
      render: (icon, record) => {
        if (record.icon_type === 'image' && icon) {
          return <img src={icon} alt="icon" style={{ width: 40, height: 40, objectFit: 'contain' }} />;
        }
        return <span>{icon || '-'}</span>;
      }
    },
    {
      title: '图标类型',
      dataIndex: 'icon_type',
      width: 100,
      render: (type) => type === 'image' ? <Tag color="blue">图片</Tag> : <Tag>图标</Tag>
    },
    {
      title: '链接类型',
      dataIndex: 'link_type',
      width: 100,
      render: (type) => type === 'internal' ? <Tag color="green">内部</Tag> : <Tag color="orange">外部</Tag>
    },
    {
      title: '跳转路径',
      dataIndex: 'link_path',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === 1}
          onChange={() => handleToggleStatus(record)}
        />
      )
    },
    {
      title: '操作',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此按钮吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="首页功能按钮管理"
      extra={
        <Space>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={handleRefreshCache}
          >
            刷新缓存
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleEdit()}
          >
            新增按钮
          </Button>
        </Space>
      }
    >
      <Table
        loading={loading}
        columns={columns}
        dataSource={buttons}
        rowKey="id"
        pagination={false}
      />

      <Modal
        title={editingButton ? '编辑按钮' : '新增按钮'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            iconType: 'image',
            linkType: 'internal',
            status: true,
            sortOrder: 0
          }}
        >
          <Form.Item
            label="按钮名称"
            name="buttonName"
            rules={[{ required: true, message: '请输入按钮名称' }]}
          >
            <Input placeholder="请输入按钮名称" maxLength={50} />
          </Form.Item>

          <Form.Item
            label="图标类型"
            name="iconType"
          >
            <Radio.Group>
              <Radio value="image">图片</Radio>
              <Radio value="icon">图标类名</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="图标URL/类名"
            name="iconUrl"
            extra="图片类型：输入图片URL；图标类型：输入图标class名"
          >
            <Input placeholder="请输入图标URL或类名" />
          </Form.Item>

          <Form.Item
            label="链接类型"
            name="linkType"
            rules={[{ required: true, message: '请选择链接类型' }]}
          >
            <Radio.Group>
              <Radio value="internal">内部路径</Radio>
              <Radio value="external">外部链接</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="跳转路径"
            name="linkPath"
            rules={[{ required: true, message: '请输入跳转路径' }]}
            extra="内部：如 /pages/mall/index；外部：如 https://example.com"
          >
            <Input placeholder="请输入跳转路径" />
          </Form.Item>

          <Form.Item
            label="排序"
            name="sortOrder"
            extra="数字越小越靠前"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default HomeFunctionButtons;
